<template>
  <v-dialog v-model="dialog" max-width="800" persistent :fullscreen="$vuetify.breakpoint.xsOnly">
    <v-card>
      <v-card-title>
        {{ $vuetify.lang.t(title) }}
        <v-spacer></v-spacer>
        <v-btn icon class="text-right" @click="clearValidation(); $emit('close-driver-dialog')">
          <v-icon>{{ closeIcon }}</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-container>
          <v-form>
            <v-row no-gutters>
              <v-col cols="12" md="6">
                <single-driver-autosearch :selected-driver.sync="entry.driver"
                                          :is-active="isDriverSearchActive"
                                          :errors="driverErrorMessages"
                                          @driver-changed="entry.driver = $event">
                </single-driver-autosearch>
              </v-col>
              <v-col cols="12" md="6" class="d-flex justify-center">
                <v-checkbox :label="$vuetify.lang.t('$vuetify.carParks.driverForm.manager')"
                            v-model="entry.role" :true-value="1" :false-value="0"></v-checkbox>
              </v-col>
            </v-row>
            <v-expand-transition>
              <div v-if="entry.role === carParkRole.MANAGER">
                <v-row no-gutters>
                  <v-col cols="12">
                    <v-checkbox :label="$vuetify.lang.t('$vuetify.carParks.driverForm.restrictedManager')"
                                v-model="entry.restricted"></v-checkbox>
                  </v-col>
                </v-row>
                <v-row no-gutters>
                  <v-col cols="12" md="6">
                    <v-switch :label="$vuetify.lang.t('$vuetify.carParks.after')" v-model="entry.isAfterUsed"
                              :disabled="entry.role === 0"></v-switch>
                    <v-row no-gutters>
                      <v-col cols="12" md="6">
                        <date-filter :value="start.date" :is-active="entry.isAfterUsed" type="start"
                                     :errors="errorStartMessages"
                                     @date-filter-changed="changeDate"></date-filter>
                      </v-col>
                      <v-col cols="12" md="6">
                        <time-filter type="start" ref="startTimeFilter" :disabled="!start.date"
                                     :is-active="entry.isAfterUsed"
                                     @time-filter-changed="changeTime"></time-filter>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-switch :label="$vuetify.lang.t('$vuetify.carParks.before')" v-model="entry.isBeforeUsed"
                              :disabled="entry.role === 0"></v-switch>
                    <v-row no-gutters>
                      <v-col cols="12" md="6">
                        <date-filter :value="finish.date" :is-active="entry.isBeforeUsed" type="finish"
                                     :errors="errorFinishMessages"
                                     @date-filter-changed="changeDate"></date-filter>
                      </v-col>
                      <v-col cols="12" md="6">
                        <time-filter type="finish" ref="finishTimeFilter" :disabled="!finish.date"
                                     :is-active="entry.isBeforeUsed"
                                     @time-filter-changed="changeTime"></time-filter>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </div>
            </v-expand-transition>
          </v-form>
        </v-container>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text @click="sendEntry">{{ $vuetify.lang.t('$vuetify.shared.send') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>

import SingleDriverAutosearch from '@/components/utils/singleDriverAutosearch'
import { mdiClose } from '@mdi/js'
import { defaultCarParkDriverEntry, getDateString, getTimeString } from '@/utils/carParks'
import DateFilter from '@/components/map/tableFilters/utils/dateFilter'
import TimeFilter from '@/components/map/tableFilters/utils/timeFilter'
import { defaultDatetimeFilterState } from '@/utils/filters'
import { zonedTimeToUtc } from 'date-fns-tz'
import { carParkRole } from '@/enum'

const mappings = {
  'start': 'after',
  'finish': 'before'
}

export default {
  name: 'carParDriverEntry',
  components: { TimeFilter, DateFilter, SingleDriverAutosearch },
  props: {
    dialog: Boolean,
    editEntry: Object,
    title: String
  },
  data: () => ({
    entry: defaultCarParkDriverEntry(),

    start: defaultDatetimeFilterState(),
    finish: defaultDatetimeFilterState(),

    errorStartMessages: [],
    errorFinishMessages: [],
    driverErrorMessages: [],

    isDriverSearchActive: true,
    closeIcon: mdiClose
  }),
  methods: {
    changeDate (data) {
      this[data.type].date = data.value
      this.entry[mappings[data.type]] = this.getDateByFilters(data.type)
    },
    changeTime (data) {
      this[data.type].time = data.value
      this.entry[mappings[data.type]] = this.getDateByFilters(data.type)
    },
    getDateByFilters (filter) {
      const date = this[filter].date
      const time = this[filter].time

      if (!date && !time) {
        return null
      } else {
        let result = ''
        if (date) {
          result = date
        }

        if (!time) {
          const timeString = filter === 'start' ? '00:00:00' : '23:59:59'
          result = result += `T${timeString}`
        } else {
          result = result += `T${time}`
        }

        return new Date(result)
      }
    },
    setDateFilter (filter, date) {
      let innerDate = null
      if (date !== null) {
        innerDate = zonedTimeToUtc(date.toISOString(), 'Europe/London')
      }

      this[filter].date = getDateString(innerDate)
      this[filter].time = getTimeString(innerDate)

      if (`${filter}TimeFilter` in this.$refs && this.$refs[`${filter}TimeFilter`]) {
        if (this[filter].time === null) {
          this.$refs[`${filter}TimeFilter`].clearState()
        } else {
          this.$refs[`${filter}TimeFilter`].setState(this[filter].time)
        }
      }
    },
    sendEntry () {
      if (this.validation()) {
        this.$emit('send-edited-entry', this.entry)
      }
    },
    validation () {
      this.errorStartMessages = []
      this.errorFinishMessages = []
      this.driverErrorMessages = []

      if (this.entry.isAfterUsed && this.entry.after === null) {
        this.errorStartMessages.push(this.$vuetify.lang.t('$vuetify.carParks.driverForm.errors.nonNullableDate'))
      }

      if (this.entry.isBeforeUsed && this.entry.before === null) {
        this.errorFinishMessages.push(this.$vuetify.lang.t('$vuetify.carParks.driverForm.errors.nonNullableDate'))
      }

      if (this.entry.after && this.entry.before) {
        const afterRaw = this.entry.after.getTime()
        const beforeRaw = this.entry.before.getTime()

        if (afterRaw > beforeRaw) {
          this.errorStartMessages.push(this.$vuetify.lang.t('$vuetify.carParks.driverForm.errors.wrongRangeOrder'))
          this.errorFinishMessages.push(this.$vuetify.lang.t('$vuetify.carParks.driverForm.errors.wrongRangeOrder'))
        }
      }

      if (!this.entry.driver) {
        this.driverErrorMessages.push(this.$vuetify.lang.t('$vuetify.carParks.driverForm.errors.nonSelectedDriver'))
      }

      return this.errorStartMessages.length === 0 && this.errorFinishMessages.length === 0 && this.driverErrorMessages.length === 0
    },
    clearValidation () {
      this.errorStartMessages = []
      this.errorFinishMessages = []
    }
  },
  watch: {
    editEntry: {
      handler (newValue) {
        this.entry = Object.assign({}, newValue)
        this.entry.driver = newValue.driver // directly trigger autocomplete search

        this.isDriverSearchActive = newValue.driver === '' || newValue.driver === null

        this.$nextTick(() => {
          this.setDateFilter('start', this.entry.after)
          this.setDateFilter('finish', this.entry.before)
        })
      },
      deep: true
    }
  },
  created () {
    this['carParkRole'] = carParkRole
  }
}
</script>

<style scoped>

</style>
